import { useEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { useNavigate, type NavigateFunction } from 'react-router-dom';
import { getSDK } from '@/store/sdkStore';
import { useCenterStore } from '@/store/centerStore';
import { t, type LocalizedValue } from '@/lib/site/i18n';
import { visibilityAllows } from '@/lib/site/visibility';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { navigateTo } from '@/lib/navigate';
import { registerWidget, type WidgetContext, type WidgetProps } from './registry';
import { useFileImage } from './fileImage';
import { SiteListBlock } from '@/components/site/SiteListBlock';

// ============================================================================
// lib/widgets/blocks.tsx — БЛОКИ САЙТОВ (Этап 1, MVP): heading/text/image/
// button/divider/spacer/hero. Единый `appearance` (фон/градиент/отступы/
// выравнивание/ширина/видимость) + `variants` + локализация значений props
// (t: локаль → default_locale). SSoT — сервер (props в layout страницы);
// здесь только рендер. Картинки — через публичные RPC файлов (гостевые права).
// ============================================================================

// ----------------------------- appearance -----------------------------

export interface BlockAppearance {
  bg?: '' | 'color' | 'gradient' | 'image';
  bgColor?: string;
  bgColorTo?: string;
  bgImage?: string; // fileId
  overlay?: number; // 0..100 (для bg=image)
  paddingY?: number; // px
  align?: '' | 'left' | 'center' | 'right';
  maxWidth?: number; // px (0 = вся ширина)
  /** Видимость (Этап 3): '' | all | desktop | mobile | auth | guest */
  visibility?: '' | 'all' | 'desktop' | 'mobile' | 'auth' | 'guest';
  /** Только роли (код роли; пусто = все) — role:<код> (кабинеты). */
  visibilityRole?: string;
  className?: string;
}

/** APPEARANCE_PROPS_SCHEMA — единый набор свойств оформления (панель редактора). */
export const APPEARANCE_PROPS_SCHEMA: Array<{ key: string; label: string; type: 'text' | 'number' | 'select' | 'image'; options?: Array<{ value: string; label: string }>; placeholder?: string; help?: string }> = [
  { key: 'bg', label: 'Фон', type: 'select', options: [
    { value: '', label: 'Нет' },
    { value: 'color', label: 'Цвет' },
    { value: 'gradient', label: 'Градиент' },
    { value: 'image', label: 'Картинка' },
  ] },
  { key: 'bgColor', label: 'Цвет фона', type: 'text', placeholder: '#f5f5f5 / @Белый' },
  { key: 'bgColorTo', label: 'Цвет градиента (2)', type: 'text', placeholder: '#ffffff' },
  { key: 'bgImage', label: 'Картинка фона', type: 'image' },
  { key: 'overlay', label: 'Затемнение (%)', type: 'number', placeholder: '0' },
  { key: 'paddingY', label: 'Отступы сверху/снизу (px)', type: 'number', placeholder: '32' },
  { key: 'align', label: 'Выравнивание', type: 'select', options: [
    { value: '', label: 'По умолчанию' },
    { value: 'left', label: 'Слева' },
    { value: 'center', label: 'По центру' },
    { value: 'right', label: 'Справа' },
  ] },
  { key: 'maxWidth', label: 'Макс. ширина (px, 0 = вся)', type: 'number', placeholder: '1200' },
  { key: 'visibility', label: 'Видимость', type: 'select', options: [
    { value: '', label: 'Все устройства' },
    { value: 'desktop', label: 'Только десктоп' },
    { value: 'mobile', label: 'Только мобильный' },
    { value: 'auth', label: 'Только авторизованным' },
    { value: 'guest', label: 'Только гостям' },
  ] },
  { key: 'visibilityRole', label: 'Только роли (код; пусто = все)', type: 'text', placeholder: 'admin / Оператор' },
  { key: 'className', label: 'CSS-класс', type: 'text' },
];

/** useBlockAppearance — appearance с учётом видимости (устройство/вход/роль). */
function useBlockAppearance(appearance: BlockAppearance | undefined): { style: CSSProperties; hidden: boolean } {
  const { isMobile } = useBreakpoint();
  const a = appearance || {};
  const { src: bgImageSrc } = useFileImage(a.bg === 'image' && a.bgImage ? a.bgImage : '');
  const hidden = !visibilityAllows(String(a.visibility || ''), String(a.visibilityRole || ''), isMobile);
  const style: CSSProperties = {};
  if (a.bg === 'color' && a.bgColor) style.background = a.bgColor;
  if (a.bg === 'gradient' && a.bgColor) style.background = `linear-gradient(135deg, ${a.bgColor}, ${a.bgColorTo || a.bgColor})`;
  if (a.bg === 'image' && bgImageSrc) {
    const o = Math.min(90, Math.max(0, a.overlay || 0)) / 100;
    style.backgroundImage = `linear-gradient(rgba(0,0,0,${o}), rgba(0,0,0,${o})), url(${bgImageSrc})`;
    style.backgroundSize = 'cover';
    style.backgroundPosition = 'center';
  }
  if (a.paddingY) style.paddingTop = a.paddingY;
  if (a.paddingY) style.paddingBottom = a.paddingY;
  return { style, hidden };
}

/** AppearanceWrap — единая обёртка блока (фон/отступы/выравнивание/ширина). */
export function AppearanceWrap({ appearance, children, className = '' }: { appearance?: BlockAppearance; children: ReactNode; className?: string }) {
  const { style, hidden } = useBlockAppearance(appearance);
  if (hidden) return null;
  const a = appearance || {};
  const inner: CSSProperties = {};
  if (a.maxWidth && a.maxWidth > 0) {
    inner.maxWidth = a.maxWidth;
    inner.marginLeft = 'auto';
    inner.marginRight = 'auto';
  }
  if (a.align === 'center') inner.textAlign = 'center';
  if (a.align === 'right') inner.textAlign = 'right';
  return (
    <section style={style} className={`w-full ${a.className || ''} ${className}`}>
      <div style={inner} className="px-4">{children}</div>
    </section>
  );
}

// ----------------------------- картинки -----------------------------

/** useAssetFileId — резолв АССЕТА сайта по КОДУ (SSoT — сервер, наследование
 *  specific→core через sdk.assets.resolveAsset). Если задан assetCode — он
 *  приоритетнее прямого fileId; при отсутствии — запасной fileId. */
export function useAssetFileId(siteCode: string, assetCode: string, fallbackFileId: string): { fileId: string; error: string } {
  const [fileId, setFileId] = useState('');
  const [error, setError] = useState('');
  useEffect(() => {
    let cancelled = false;
    setFileId(''); setError('');
    if (!assetCode) return;
    if (!siteCode) { setError('не указан сайт для ассета'); return; }
    (async () => {
      try {
        const r = await getSDK().assets.resolveAsset({ site: siteCode, code: assetCode });
        if (cancelled) return;
        if (r.found && r.fileId) setFileId(r.fileId);
        else setError(`ассет «${assetCode}» не найден`);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'не удалось получить ассет');
      }
    })();
    return () => { cancelled = true; };
  }, [siteCode, assetCode]);
  if (assetCode) return { fileId, error };
  return { fileId: fallbackFileId, error: '' };
}

// ----------------------------- блоки -----------------------------

function blockT(props: WidgetProps, ctx: WidgetContext, key: string): string {
  return siteBlockText(props, ctx, key);
}

/** goAppTarget — переход в ПРИЛОЖЕНИЕ платформы (существующий маршрут).
 *  Схема ссылки блока: `app:<код>` (например, app:market_client).
 *  Опубликованная база — state-based публикация (/enterprise/{slug},
 *  state.app): вход сохраняет намерение, после входа открывается приложение.
 *  Иначе — конфигураторский маршрут /designer/app/<код>. */
function goAppTarget(navigate: NavigateFunction, appCode: string): void {
  const code = appCode.trim();
  if (!code) return;
  const { baseSlug, basePublished } = useCenterStore.getState();
  if (baseSlug && basePublished) {
    navigate(`/enterprise/${baseSlug}`, { state: { app: code } });
    return;
  }
  navigate(`/designer/app/${code}`);
}

/** siteBlockText — текст блока: props → перевод `site.{code}.{key}` (Этап 3).
 *  Перевод используется, когда значение props пустое (реестр «Переводы»). */
export function siteBlockText(props: WidgetProps, ctx: WidgetContext, key: string): string {
  const direct = t(props[key] as LocalizedValue, ctx.siteLocale || '', ctx.siteDefaultLocale || '', ctx.record);
  if (direct) return direct;
  const translations = ctx.translations || {};
  return translations[`site.${ctx.siteCode || ''}.${key}`] || '';
}

function HeadingBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const text = blockT(props, ctx, 'text');
  const level = Math.min(6, Math.max(1, Number(props.level) || 2));
  const Tag = (`h${level}`) as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
  const sizes: Record<number, string> = { 1: 'text-4xl md:text-5xl', 2: 'text-3xl md:text-4xl', 3: 'text-2xl md:text-3xl', 4: 'text-xl md:text-2xl', 5: 'text-lg', 6: 'text-base' };
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <Tag className={`font-semibold tracking-tight text-[var(--site-text,inherit)] ${sizes[level]} py-1`}>{text}</Tag>
    </AppearanceWrap>
  );
}

/** SiteTextBlock — блок «Текст» сайта (нативный рендер: без карточек-оболочек). */
export function SiteTextBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const text = blockT(props, ctx, 'text');
  const align = String(props.align || (props.appearance as BlockAppearance | undefined)?.align || '');
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <p className="whitespace-pre-wrap leading-relaxed text-[15px] text-[var(--site-text,inherit)]"
        style={{ textAlign: align === 'center' ? 'center' : align === 'right' ? 'right' : undefined }}>
        {text}
      </p>
    </AppearanceWrap>
  );
}

function ImageBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const assetCode = String(props.assetCode || '');
  const { fileId, error: assetError } = useAssetFileId(ctx.siteCode || '', assetCode, String(props.fileId || ''));
  const { src, error } = useFileImage(fileId);
  const width = Number(props.width) || 0;
  // Вид варианта влияет на форму картинки (вариант — единый механизм реестра).
  const variant = String(props.variant || '');
  const shape = variant === 'circle' ? 'rounded-full aspect-square object-cover' : variant === 'square' ? 'rounded-none' : 'rounded-lg';
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      {!fileId ? (
        <div className={`text-sm py-4 ${assetError ? 'text-destructive' : 'text-muted-foreground'}`}>
          {assetError || 'Картинка не выбрана'}
        </div>
      ) : error ? (
        <div className="text-sm text-destructive py-4">Не удалось загрузить изображение: {error}</div>
      ) : !src ? (
        <div className="text-sm text-muted-foreground py-4">Загрузка изображения…</div>
      ) : (
        <img src={src} alt={String(props.alt || '')} className={shape}
          style={{ maxWidth: width > 0 ? width : variant === 'circle' ? 240 : '100%', width: width > 0 ? width : undefined, height: 'auto' }} />
      )}
    </AppearanceWrap>
  );
}

function ButtonBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const navigate = useNavigate();
  const label = blockT(props, ctx, 'label');
  const href = String(props.href || '');
  const variant = String(props.variant || 'primary');
  const styles: Record<string, string> = {
    primary: 'bg-[var(--site-primary,var(--color-primary))] text-[var(--site-primary-foreground,#fff)]',
    outline: 'border border-[var(--site-primary,var(--color-primary))] text-[var(--site-primary,var(--color-primary))]',
    ghost: 'text-[var(--site-text,inherit)]',
  };
  const go = () => {
    if (href.startsWith('app:')) { goAppTarget(navigate, href.slice(4)); return; }
    if (/^https?:\/\//i.test(href)) { window.open(href, '_blank', 'noopener'); return; }
    if (href.startsWith('/')) { navigateTo(href); return; }
    // slug страницы сайта: 'about' → /p/{site}/about (локаль по умолчанию)
    const base = `/p/${ctx.siteCode || ''}`;
    const siteHref = href ? `${base}/${href.replace(/^\/+/, '')}` : base;
    if (ctx.siteCode) navigateTo(siteHref);
  };
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <button type="button" onClick={go} className={`inline-flex items-center rounded-lg px-5 py-2.5 text-sm font-medium transition-opacity hover:opacity-90 ${styles[variant] || styles.primary}`}>
        {label || 'Кнопка'}
      </button>
    </AppearanceWrap>
  );
}

function DividerBlock({ props }: { props: WidgetProps }) {
  const thickness = Math.max(1, Number(props.thickness) || 1);
  const color = String(props.color || 'var(--site-border, var(--color-border))');
  const variant = String(props.variant || '');
  const borderStyle: CSSProperties['borderTopStyle'] =
    variant === 'dashed' ? 'dashed' : variant === 'dotted' ? 'dotted' : 'solid';
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <hr style={{ borderTopWidth: thickness, borderColor: color, borderTopStyle: borderStyle }} className="border-0 border-t" />
    </AppearanceWrap>
  );
}

function SpacerBlock({ props }: { props: WidgetProps }) {
  const height = Math.max(4, Number(props.height) || 32);
  return <div style={{ height }} aria-hidden="true" />;
}

function HeroBlock({ props, ctx }: { props: WidgetProps; ctx: WidgetContext }) {
  const navigate = useNavigate();
  const title = blockT(props, ctx, 'title');
  const subtitle = blockT(props, ctx, 'subtitle');
  const buttonLabel = blockT(props, ctx, 'buttonLabel');
  const { fileId: imageId } = useAssetFileId(ctx.siteCode || '', String(props.assetCode || ''), String(props.image || ''));
  const { src } = useFileImage(imageId);
  const height = Math.max(120, Number(props.height) || 360);
  const overlay = Math.min(90, Math.max(0, Number(props.overlay) || 35));
  return (
    <AppearanceWrap appearance={props.appearance as BlockAppearance}>
      <div className="relative flex items-center justify-center rounded-xl overflow-hidden"
        style={{ minHeight: height, background: src ? `center / cover no-repeat url(${src})` : 'var(--site-primary, var(--color-primary))' }}>
        <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${overlay / 100})` }} />
        <div className="relative z-10 text-center px-6 py-10 max-w-3xl">
          <h1 className="text-3xl md:text-5xl font-bold text-white">{title}</h1>
          {subtitle && <p className="mt-3 text-white/90 text-lg whitespace-pre-wrap">{subtitle}</p>}
          {buttonLabel && (
            <button type="button" className="mt-6 inline-flex items-center rounded-lg px-6 py-3 text-sm font-medium bg-white text-black hover:opacity-90"
              onClick={() => {
                const href = String(props.buttonHref || '');
                if (href.startsWith('app:')) { goAppTarget(navigate, href.slice(4)); return; }
                if (/^https?:\/\//i.test(href)) { window.open(href, '_blank', 'noopener'); return; }
                if (href.startsWith('/')) { navigateTo(href); return; }
                if (ctx.siteCode) navigateTo(href ? `/p/${ctx.siteCode}/${href}` : `/p/${ctx.siteCode}`);
              }}>
              {buttonLabel}
            </button>
          )}
        </div>
      </div>
    </AppearanceWrap>
  );
}

// ----------------------------- регистрация -----------------------------

/** registerSiteBlocks — блоки сайтов (вызывается из registerBuiltinWidgets). */
export function registerSiteBlocks(): void {
  registerWidget({
    type: 'heading', display: 'Заголовок', description: 'Заголовок H1–H6', siteOk: true, appearanceSchema: true,
    variants: [
      { value: '', label: 'Обычный' },
      { value: 'uppercase', label: 'Заглавные' },
      { value: 'accent', label: 'Акцентный (цвет темы)' },
    ],
    propsSchema: [
      { key: 'text', label: 'Текст', type: 'text' },
      { key: 'level', label: 'Уровень (1–6)', type: 'number', placeholder: '2' },
      { key: 'align', label: 'Выравнивание', type: 'select', options: [
        { value: '', label: 'По умолчанию' }, { value: 'left', label: 'Слева' }, { value: 'center', label: 'По центру' }, { value: 'right', label: 'Справа' },
      ] },
    ],
    render: (props, ctx) => <HeadingBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'image', display: 'Картинка', description: 'Изображение из файлов базы', siteOk: true, appearanceSchema: true,
    variants: [
      { value: '', label: 'Скруглённая' },
      { value: 'square', label: 'Без скругления' },
      { value: 'circle', label: 'Круглая' },
    ],
    propsSchema: [
      { key: 'fileId', label: 'Файл (id)', type: 'image' },
      { key: 'assetCode', label: 'Ассет сайта (код)', type: 'text', help: 'Код ассета: свой сайтовый, иначе общий core (приоритетнее файла)' },
      { key: 'alt', label: 'Подпись (alt)', type: 'text' },
      { key: 'width', label: 'Ширина (px, 0 = 100%)', type: 'number', placeholder: '0' },
    ],
    render: (props, ctx) => <ImageBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'button', display: 'Кнопка', description: 'Кнопка-ссылка', siteOk: true, appearanceSchema: true,
    variants: [
      { value: 'primary', label: 'Основная' },
      { value: 'outline', label: 'Контурная' },
      { value: 'ghost', label: 'Прозрачная' },
    ],
    propsSchema: [
      { key: 'label', label: 'Надпись', type: 'text' },
      { key: 'href', label: 'Ссылка (slug / /путь / app:<код> / https://…)', type: 'text', help: 'slug страницы сайта, внутренний путь, приложение платформы (app:market_client) или внешний URL' },
    ],
    render: (props, ctx) => <ButtonBlock props={props} ctx={ctx} />,
  });
  registerWidget({
    type: 'divider', display: 'Разделитель', description: 'Горизонтальная линия', siteOk: true, appearanceSchema: true,
    variants: [
      { value: '', label: 'Сплошная' },
      { value: 'dashed', label: 'Пунктирная' },
      { value: 'dotted', label: 'Точечная' },
    ],
    propsSchema: [
      { key: 'color', label: 'Цвет', type: 'text', placeholder: 'var(--site-border)' },
      { key: 'thickness', label: 'Толщина (px)', type: 'number', placeholder: '1' },
    ],
    render: (props) => <DividerBlock props={props} />,
  });
  registerWidget({
    type: 'spacer', display: 'Отступ', description: 'Пустое пространство', siteOk: true,
    propsSchema: [{ key: 'height', label: 'Высота (px)', type: 'number', placeholder: '32' }],
    render: (props) => <SpacerBlock props={props} />,
  });
  // Примитив ДВИЖКА РЕНДЕРА (SSoT/SRP): публичный список записей для сайтов —
  // данные с сервера (SiteService.ListSiteRecords: таблица/схема/поля/права
  // «Гостя»), оформление — токенами сайта; пресеты cards/rows/tiles.
  registerWidget({
    type: 'site_list', display: 'Список записей (сайт)', description: 'Публичный список карточек/строк по объекту (сервер — SSoT)', siteOk: true, appearanceSchema: true,
    variants: [
      { value: 'cards', label: 'Карточки (панели)' },
      { value: 'rows', label: 'Строки (сайте)' },
      { value: 'tiles', label: 'Плитки (компактно)' },
    ],
    propsSchema: [
      { key: 'objectType', label: 'Тип', type: 'select', options: [
        { value: 'catalog', label: 'Справочник' },
        { value: 'document', label: 'Документ' },
        { value: 'register', label: 'Регистр' },
        { value: 'task_type', label: 'Тип задачи' },
        { value: 'order_type', label: 'Тип заявки' },
      ] },
      { key: 'objectCode', label: 'Код объекта (таблица/код типа)', type: 'text', placeholder: 'quantum_products' },
      { key: 'columns', label: 'Поля карточки (CSV)', type: 'text', placeholder: 'name,category' },
      { key: 'titleField', label: 'Поле заголовка', type: 'text', placeholder: 'name' },
      { key: 'imageField', label: 'Поле картинки (id файла)', type: 'text', placeholder: 'image' },
      { key: 'limit', label: 'Записей на странице', type: 'number', placeholder: '12' },
      { key: 'itemUrl', label: 'Ссылка записи (itemUrl)', type: 'text', placeholder: '/p/quantum/products/{ref}' },
      { key: 'filters', label: 'Постоянные отборы', type: 'filters', help: 'применяются всегда (сервер фильтрует)' },
      { key: 'filterFields', label: 'Поля отбора для посетителя (CSV)', type: 'text', placeholder: 'category,status_code', help: 'селекты/поля в панели над списком; состояние — в адресе страницы' },
      { key: 'searchField', label: 'Поле поиска', type: 'text', placeholder: 'name', help: 'строка поиска (содержит) по этому полю' },
      { key: 'searchPlaceholder', label: 'Подсказка поиска', type: 'text', placeholder: 'Поиск…' },
      { key: 'sortFields', label: 'Поля сортировки (CSV)', type: 'text', placeholder: 'name,created_at', help: 'выбор поля и направления в панели' },
      { key: 'sort', label: 'Сортировка по умолчанию', type: 'text', placeholder: 'name' },
      { key: 'sortDesc', label: 'По убыванию (по умолчанию)', type: 'boolean' },
      { key: 'showPager', label: 'Показывать страницы', type: 'boolean', help: 'выключено по умолчанию; включите для длинных списков (иначе выводится только limit записей)' },
      { key: 'emptyText', label: 'Текст «нет записей»', type: 'text', placeholder: 'Записей нет' },
      { key: 'gap', label: 'Отступ сетки (px)', type: 'number', placeholder: '20' },
      { key: 'columnsCount', label: 'Колонок в ряду (0 = авто)', type: 'number', placeholder: '0' },
      { key: 'radius', label: 'Радиус (px)', type: 'number', placeholder: '12' },
      { key: 'padding', label: 'Внутренние отступы (px)', type: 'number', placeholder: '20' },
    ],
    render: (props) => <SiteListBlock props={props as never} />,
  });
  registerWidget({
    type: 'hero', display: 'Обложка (hero)', description: 'Крупный первый экран: картинка/фон + заголовок + кнопка', siteOk: true, island: true,
    propsSchema: [
      { key: 'title', label: 'Заголовок', type: 'text' },
      { key: 'subtitle', label: 'Подзаголовок', type: 'text' },
      { key: 'buttonLabel', label: 'Надпись кнопки', type: 'text' },
      { key: 'buttonHref', label: 'Ссылка кнопки', type: 'text', help: 'slug страницы сайта, внутренний путь, приложение платформы (app:market_client) или внешний URL' },
      { key: 'image', label: 'Картинка (fileId)', type: 'image' },
      { key: 'assetCode', label: 'Ассет сайта (код)', type: 'text', help: 'Код ассета: свой сайтовый, иначе общий core (приоритетнее файла)' },
      { key: 'height', label: 'Высота (px)', type: 'number', placeholder: '360' },
      { key: 'overlay', label: 'Затемнение (%)', type: 'number', placeholder: '35' },
    ],
    render: (props, ctx) => <HeroBlock props={props} ctx={ctx} />,
  });
}
